import React, { useId, useState } from 'react'
import Button from 'components/button'
import Calendar from 'components/calendar'
import Popover, { popoverPlacement } from 'components/popover'
import { Calendar as CalendarIcon } from 'components/icons'
import { IContextMenuState } from 'types'

// The month's seven 32px days and the popover's 12px either side, inside its border.
const WIDTH = 250
const HEIGHT = 320

/** A day written yyyy-mm-dd, read as that day where the reader is, not as midnight UTC. */
export const parseDay = (day: string): Date | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(day)
  if (!match) return undefined
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

/** A day as yyyy-mm-dd. */
export const formatDay = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

interface DatePickerProps {
  /** The day chosen, yyyy-mm-dd, or '' for none. */
  value: string
  onChange: (day: string) => void
  /** The button's accessible name. */
  label?: string
  /** The id of what names the field the button is in, read after the button's own name ("Choose a date Due"). */
  labelledBy?: string
  disabled?: boolean
  className?: string
}

/**
 * A calendar button, in the manner of shadcn/ui's date picker: it opens a month (the Calendar) in a popover, on the
 * day chosen or this month, and picking a day closes it. It sits at the end of a field the day can also be typed in.
 */
const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  label = 'Choose a date',
  labelledBy,
  disabled,
  className
}) => {
  const [at, setAt] = useState<IContextMenuState | null>(null)
  const id = useId()
  const selected = parseDay(value)

  return (
    <>
      <Button
        internalType="transparent"
        id={id}
        aria-label={label}
        aria-labelledby={labelledBy ? `${id} ${labelledBy}` : undefined}
        aria-haspopup="dialog"
        aria-expanded={!!at}
        disabled={disabled}
        className={`size-7 shrink-0 p-0 ${className ?? ''}`}
        // The popover closes on a press outside it, and this button counts as outside: while it's open, keep the
        // press from reaching it and let the click close it instead.
        onMouseDown={(event: React.MouseEvent) => {
          if (at) event.stopPropagation()
        }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation()
          const field = (event.currentTarget.closest('[data-date-field]') as HTMLElement | null) ?? event.currentTarget
          setAt(
            at ? null : popoverPlacement(field.getBoundingClientRect(), { align: 'end', width: WIDTH, height: HEIGHT })
          )
        }}
      >
        <CalendarIcon />
      </Button>
      <Popover id="date-picker" at={at} onClose={() => setAt(null)} label={label} width={WIDTH}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          // Into the month as it opens, on the day chosen or today, so the arrow keys move through the days.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          onSelect={(date) => {
            if (date) onChange(formatDay(date))
            setAt(null)
          }}
        />
      </Popover>
    </>
  )
}

export default DatePicker
